import { useCallback, useEffect } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CoreFormFieldDate } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldDate';
import { CoreFormFieldText } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldText';
import { CoreFormFieldTextArea } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldTextArea';
import { DateTime } from 'luxon';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';

import { useAppSelector } from 'src/app/store/hooks';
import { DriverSelectionField } from 'src/common/DriverSelectionField';
import { useDriversRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import CoreDaySelectorFormField from 'src/common/FormField/v2/core/CoreDaySelectorFormField';
import { CoreFormFieldSwitch } from 'src/common/FormField/v2/core/CoreFormFieldSwitch';
import { ModalFooter } from 'src/common/primitives/Modal/ModalFooter';
import { formatFullName } from 'src/common/util/formatFullName';

import {
  convertDaysOfWeekToLocalDays,
  convertLocalDaysToDaysOfWeek,
} from './daysOfWeekMaps';
import { RouteTemplateFormValues } from './types';
import { useSubmitRouteTemplateForm } from './useSubmitRouteTemplateForm';

interface RouteTemplateFormStep1Props {
  onNext: () => void;
  onCancel: () => void;
  setStep: (step: number) => void;
}
export function RouteTemplateFormStep1({
  onNext,
  onCancel,
  setStep,
}: RouteTemplateFormStep1Props) {
  const form = useFormContext<RouteTemplateFormValues>();

  const defaultDriver = form.getValues('default_driver');
  const defaultDriverId = form.getValues('default_driver_id');
  const workflow = useAppSelector((state) => state.routeTemplates.workflow);
  const canSelectDriver = workflow === 'single';
  const assignments = useAppSelector(
    (state) => state.routeTemplates.bulkCreationAssignments
  );
  const canCreateMore = assignments.length > 1;

  const { data: driverProfile, isLoading: isLoadingDriverProfile } =
    useDriversRetrieveQuery(
      {
        id: defaultDriverId,
      },
      {
        skip: defaultDriver?.value === defaultDriverId,
      }
    );

  useEffect(() => {
    if (!driverProfile) {
      return;
    }

    form.setValue('default_driver', {
      value: defaultDriverId,
      label: formatFullName(driverProfile),
    });
  }, [defaultDriverId, driverProfile, form]);

  const validateForm = useCallback(async () => {
    try {
      const result = await form.trigger([
        'name',
        'start_time',
        'end_time',
        'default_driver',
      ]);

      return result;
    } catch (e) {
      // Don't do anything, form is not valid
    }

    return false;
  }, [form]);

  const handleClickNext = useCallback(async () => {
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    onNext();
  }, [onNext, validateForm]);

  // While in bulk create mode, we need to save the form

  const { saveAndClose, saveAndNext } = useSubmitRouteTemplateForm(
    false,
    [],
    [],
    () => {},
    () => {}
  );

  const handleSaveAndClose = useCallback<
    SubmitHandler<RouteTemplateFormValues>
  >(
    async (data) => {
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      await saveAndClose(data);
    },
    [saveAndClose, validateForm]
  );

  const handleSaveAndNext = useCallback<SubmitHandler<RouteTemplateFormValues>>(
    async (data) => {
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      await saveAndNext(data);
      setStep(1);
    },
    [saveAndNext, setStep, validateForm]
  );
  return (
    <div className="mt-4 flex flex-col gap-4">
      <p className="text-sm text-gray-500">
        Define the name and schedule for this template.
      </p>
      <Controller
        control={form.control}
        name="name"
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState }) => {
          return (
            <CoreFormFieldText
              label="Name *"
              inputProps={field}
              error={fieldState.error?.message}
            />
          );
        }}
      />
      <Controller
        control={form.control}
        name="notes"
        render={({ field, fieldState }) => {
          return (
            <CoreFormFieldTextArea
              label="Notes"
              inputProps={field}
              error={fieldState.error?.message}
            />
          );
        }}
      />
      <DriverSelectionField
        fieldName="default_driver"
        disabled={!canSelectDriver}
        onChange={(value) => {
          if (value) {
            form.setValue('default_driver_id', value.value);
          }
        }}
      />
      <Controller
        control={form.control}
        name="allow_rbr_trips"
        render={({ field, fieldState }) => {
          return (
            <CoreFormFieldSwitch
              label="Allow additional trip assignments with Run Bambi Run"
              inputProps={{
                value: field.value,
                onChange: field.onChange,
              }}
              error={fieldState.error?.message}
            />
          );
        }}
      />
      <p className="text-sm text-gray-500">
        Optional settings that can help select which Route Template to use.
      </p>
      <div className="flex flex-row justify-between gap-2">
        <Controller
          control={form.control}
          name="range_start"
          render={({ field, fieldState }) => {
            const value = field.value === null ? '' : field.value;
            return (
              <CoreFormFieldDate
                label="Start date"
                inputProps={{
                  ...field,
                  value: value
                    ? DateTime.fromISO(value).toFormat('yyyy-MM-dd')
                    : '',
                }}
                error={fieldState.error?.message}
              />
            );
          }}
        />
        <Controller
          control={form.control}
          name="range_end"
          render={({ field, fieldState }) => {
            const value = field.value === null ? '' : field.value;
            return (
              <CoreFormFieldDate
                label="End date"
                inputProps={{
                  ...field,
                  value: value
                    ? DateTime.fromISO(value).toFormat('yyyy-MM-dd')
                    : '',
                }}
                error={fieldState.error?.message}
              />
            );
          }}
        />
      </div>
      <Controller
        control={form.control}
        name="days_of_week"
        render={({ field, fieldState }) => {
          return (
            <CoreDaySelectorFormField
              label="Available on Days"
              inputProps={{
                onChange: (e) => {
                  field.onChange(convertLocalDaysToDaysOfWeek(e));
                },
                value: convertDaysOfWeekToLocalDays(field.value ?? []),
              }}
              error={fieldState.error?.message}
            />
          );
        }}
      />
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        {workflow === 'single' ? (
          <Button
            variant="primary"
            onClick={handleClickNext}
            disabled={isLoadingDriverProfile}
          >
            Next
          </Button>
        ) : canCreateMore ? (
          <div className="flex flex-row gap-2">
            <Button
              variant="primary-outline"
              onClick={form.handleSubmit(handleSaveAndClose)}
              disabled={isLoadingDriverProfile}
            >
              Save and Close
            </Button>
            <Button
              variant="primary"
              onClick={form.handleSubmit(handleSaveAndNext)}
              disabled={isLoadingDriverProfile}
            >
              Save and Create more
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={form.handleSubmit(handleSaveAndClose)}
            disabled={isLoadingDriverProfile}
          >
            Save
          </Button>
        )}
      </ModalFooter>
    </div>
  );
}
